import { ArrowLeftOutlined, LinkOutlined } from '@ant-design/icons';
import { Button, Card, Descriptions, Empty, Row, Col, Statistic, Table, Tabs, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import IncidentListTable from '../components/IncidentListTable';
import {
  getFactory,
  listIncidentAttachments,
  listIncidents,
  listIncidentTasks,
} from '../services/api';
import type {
  CurrentUser,
  FactoryDetail,
  FactoryProductSummary,
  Incident,
  IncidentAttachment,
  IncidentStatus,
  IncidentTask,
} from '../types/incident';

const { Text } = Typography;

interface Props {
  currentUser: CurrentUser | null;
}

type MonthBucket = {
  key: string;
  label: string;
  total: number;
  statuses: Record<IncidentStatus, number>;
};

type StatusSlice = {
  status: IncidentStatus;
  count: number;
  percent: number;
  offset: number;
};

type DefectHeatmapRow = {
  label: string;
  counts: Record<string, number>;
  total: number;
};

const STATUS_ORDER: IncidentStatus[] = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'];
const STATUS_COLORS: Record<IncidentStatus, string> = {
  DRAFT: '#9ca3af',
  OPEN: '#ef4444',
  IN_PROGRESS: '#f59e0b',
  RESOLVED: '#2563eb',
  CLOSED: '#16a34a',
};

const monthKeyFromDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (key: string) => {
  const [year, month] = key.split('-');
  return `${year}/${month}`;
};

const getIncidentMonthKey = (incident: Incident) =>
  monthKeyFromDate(incident.production_lot_date) ||
  monthKeyFromDate(incident.production_factory_setting_date) ||
  monthKeyFromDate(incident.trouble_date);

const getRecentIncidents = (incidents: Incident[]) => {
  const dated = incidents
    .map((incident) => ({ incident, key: getIncidentMonthKey(incident) }))
    .filter((item): item is { incident: Incident; key: string } => Boolean(item.key));
  if (dated.length === 0) return incidents;

  const latestKey = dated.reduce((latest, item) => (item.key > latest ? item.key : latest), dated[0].key);
  const [latestYear, latestMonth] = latestKey.split('-').map(Number);
  const cutoff = new Date(latestYear, latestMonth - 6, 1);

  return dated
    .filter((item) => {
      const [year, month] = item.key.split('-').map(Number);
      return new Date(year, month - 1, 1) >= cutoff;
    })
    .map((item) => item.incident);
};

const buildMonthlyBuckets = (incidents: Incident[]): MonthBucket[] => {
  const map = new Map<string, MonthBucket>();
  incidents.forEach((incident) => {
    const key = getIncidentMonthKey(incident);
    if (!key) return;
    const bucket =
      map.get(key) ||
      {
        key,
        label: monthLabel(key),
        total: 0,
        statuses: { DRAFT: 0, OPEN: 0, IN_PROGRESS: 0, RESOLVED: 0, CLOSED: 0 },
      };
    bucket.total += 1;
    bucket.statuses[incident.status] += 1;
    map.set(key, bucket);
  });
  return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
};

const buildStatusSlices = (incidents: Incident[]): StatusSlice[] => {
  const total = incidents.length || 1;
  let offset = 0;
  return STATUS_ORDER.map((status) => {
    const count = incidents.filter((incident) => incident.status === status).length;
    const percent = (count / total) * 100;
    const slice = { status, count, percent, offset };
    offset += percent;
    return slice;
  });
};

const buildDefectHeatmap = (incidents: Incident[], months: MonthBucket[], language: string): DefectHeatmapRow[] => {
  const rows = new Map<string, DefectHeatmapRow>();
  incidents.forEach((incident) => {
    const key = getIncidentMonthKey(incident);
    if (!key) return;
    const labels = incident.defect_tags
      .filter((tag) => tag.category === 'LOCATION')
      .map((tag) => (language.startsWith('ja') ? tag.label_ja : tag.label_en) || tag.label_ja || tag.label_en);
    const effectiveLabels = labels.length > 0 ? labels : ['Unspecified'];
    effectiveLabels.forEach((label) => {
      const row = rows.get(label) || { label, counts: {}, total: 0 };
      row.counts[key] = (row.counts[key] || 0) + 1;
      row.total += 1;
      rows.set(label, row);
    });
  });

  return Array.from(rows.values())
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label))
    .slice(0, 8)
    .map((row) => ({
      ...row,
      counts: Object.fromEntries(months.map((month) => [month.key, row.counts[month.key] || 0])),
    }));
};

const maxCount = (values: number[]) => Math.max(1, ...values);

const MonthlyIncidentChart = ({ buckets }: { buckets: MonthBucket[] }) => {
  const { t } = useTranslation();
  const chartWidth = 720;
  const chartHeight = 260;
  const padding = { top: 20, right: 20, bottom: 44, left: 36 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;
  const maxTotal = maxCount(buckets.map((bucket) => bucket.total));
  const barGap = 10;
  const barWidth = buckets.length > 0 ? Math.max(18, (innerWidth - barGap * (buckets.length - 1)) / buckets.length) : 0;

  if (buckets.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('factory.summary.noChartData')} />;
  }

  return (
    <div className="factoryChartWrap">
      <svg className="factoryBarChart" viewBox={`0 0 ${chartWidth} ${chartHeight}`} role="img" aria-label={t('factory.summary.monthlyTrend')}>
        <line x1={padding.left} y1={padding.top + innerHeight} x2={chartWidth - padding.right} y2={padding.top + innerHeight} className="factoryChartAxis" />
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + innerHeight} className="factoryChartAxis" />
        {[0, 0.5, 1].map((ratio) => {
          const y = padding.top + innerHeight - innerHeight * ratio;
          const value = Math.round(maxTotal * ratio);
          return (
            <g key={ratio}>
              <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} className="factoryChartGrid" />
              <text x={padding.left - 8} y={y + 4} textAnchor="end" className="factoryChartTick">{value}</text>
            </g>
          );
        })}
        {buckets.map((bucket, index) => {
          const x = padding.left + index * (barWidth + barGap);
          let y = padding.top + innerHeight;
          return (
            <g key={bucket.key}>
              {STATUS_ORDER.map((status) => {
                const value = bucket.statuses[status];
                const height = (value / maxTotal) * innerHeight;
                y -= height;
                return value > 0 ? (
                  <rect key={status} x={x} y={y} width={barWidth} height={height} fill={STATUS_COLORS[status]} rx={3} />
                ) : null;
              })}
              <text x={x + barWidth / 2} y={chartHeight - 18} textAnchor="middle" className="factoryChartTick">
                {bucket.label.slice(2)}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="factoryChartLegend">
        {STATUS_ORDER.map((status) => (
          <span key={status} className="factoryChartLegendItem">
            <span className="factoryChartLegendSwatch" style={{ background: STATUS_COLORS[status] }} />
            {t(`incident.status.${status}`)}
          </span>
        ))}
      </div>
    </div>
  );
};

const RecentStatusDonut = ({ slices }: { slices: StatusSlice[] }) => {
  const { t } = useTranslation();
  const total = slices.reduce((sum, slice) => sum + slice.count, 0);
  const radius = 43;
  const circumference = 2 * Math.PI * radius;

  if (total === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('factory.summary.noRecentData')} />;
  }

  return (
    <div className="factoryDonutLayout">
      <svg className="factoryDonutChart" viewBox="0 0 120 120" role="img" aria-label={t('factory.summary.recentStatusRatio')}>
        <circle cx="60" cy="60" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="16" />
        {slices.map((slice) => (
          <circle
            key={slice.status}
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={STATUS_COLORS[slice.status]}
            strokeWidth="16"
            strokeDasharray={`${(slice.percent / 100) * circumference} ${circumference}`}
            strokeDashoffset={-(slice.offset / 100) * circumference}
            transform="rotate(-90 60 60)"
          />
        ))}
        <text x="60" y="56" textAnchor="middle" className="factoryDonutTotal">{total}</text>
        <text x="60" y="73" textAnchor="middle" className="factoryDonutCaption">{t('factory.summary.incidents')}</text>
      </svg>
      <div className="factoryDonutLegend">
        {slices.map((slice) => (
          <div key={slice.status} className="factoryDonutLegendItem">
            <span className="factoryChartLegendSwatch" style={{ background: STATUS_COLORS[slice.status] }} />
            <span>{t(`incident.status.${slice.status}`)}</span>
            <strong>{slice.count}</strong>
          </div>
        ))}
      </div>
    </div>
  );
};

const DefectHeatmap = ({ rows, months }: { rows: DefectHeatmapRow[]; months: MonthBucket[] }) => {
  const { t } = useTranslation();
  const maxValue = maxCount(rows.flatMap((row) => months.map((month) => row.counts[month.key] || 0)));
  if (rows.length === 0 || months.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('factory.summary.noChartData')} />;
  }

  return (
    <div className="factoryHeatmap">
      <div className="factoryHeatmapHeader" style={{ gridTemplateColumns: `160px repeat(${months.length}, minmax(34px, 1fr))` }}>
        <span />
        {months.map((month) => <span key={month.key}>{month.label.slice(2)}</span>)}
      </div>
      {rows.map((row) => (
        <div key={row.label} className="factoryHeatmapRow" style={{ gridTemplateColumns: `160px repeat(${months.length}, minmax(34px, 1fr))` }}>
          <span className="factoryHeatmapLabel">{row.label}</span>
          {months.map((month) => {
            const value = row.counts[month.key] || 0;
            const intensity = value / maxValue;
            return (
              <span
                key={month.key}
                className="factoryHeatmapCell"
                style={{ backgroundColor: `rgba(37, 99, 235, ${0.08 + intensity * 0.72})` }}
                title={`${row.label} ${month.label}: ${value}`}
              >
                {value || ''}
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const FactoryDetailPage = ({ currentUser }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { factoryId } = useParams();
  const [factory, setFactory] = useState<FactoryDetail | null>(null);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [tasksByIncident, setTasksByIncident] = useState<Record<string, IncidentTask[]>>({});
  const [attachmentsByIncident, setAttachmentsByIncident] = useState<Record<string, IncidentAttachment[]>>({});

  useEffect(() => {
    if (!factoryId) return;
    let cancelled = false;
    Promise.all([
      getFactory(factoryId),
      listIncidents({ factory_id: factoryId }),
    ])
      .then(async ([factoryItem, incidentItems]) => {
        const related = await Promise.all(
          incidentItems.map(async (incident) => {
            const [tasks, attachments] = await Promise.all([
              listIncidentTasks(incident.id).catch(() => []),
              listIncidentAttachments(incident.id).catch(() => []),
            ]);
            return { incidentId: incident.id, tasks, attachments };
          })
        );
        if (cancelled) return;
        setFactory(factoryItem);
        setIncidents(incidentItems);
        setTasksByIncident(Object.fromEntries(related.map((item) => [item.incidentId, item.tasks])));
        setAttachmentsByIncident(Object.fromEntries(related.map((item) => [item.incidentId, item.attachments])));
      })
      .catch(() => {
        if (!cancelled) {
          setFactory(null);
          setIncidents([]);
          setTasksByIncident({});
          setAttachmentsByIncident({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [factoryId]);

  const factoryListPath =
    typeof location.state === 'object' &&
    location.state !== null &&
    'factoryListPath' in location.state &&
    typeof location.state.factoryListPath === 'string'
      ? location.state.factoryListPath
      : '/factories';

  const monthlyBuckets = useMemo(() => buildMonthlyBuckets(incidents), [incidents]);
  const recentIncidents = useMemo(() => getRecentIncidents(incidents), [incidents]);
  const recentSlices = useMemo(() => buildStatusSlices(recentIncidents), [recentIncidents]);
  const heatmapRows = useMemo(() => buildDefectHeatmap(incidents, monthlyBuckets, i18n.language), [incidents, i18n.language, monthlyBuckets]);

  const previousPeriodCount = useMemo(() => {
    const dated = incidents
      .map((incident) => ({ incident, key: getIncidentMonthKey(incident) }))
      .filter((item): item is { incident: Incident; key: string } => Boolean(item.key));
    if (dated.length === 0) return 0;
    const latestKey = dated.reduce((latest, item) => (item.key > latest ? item.key : latest), dated[0].key);
    const [latestYear, latestMonth] = latestKey.split('-').map(Number);
    const recentCutoff = new Date(latestYear, latestMonth - 6, 1);
    const previousCutoff = new Date(latestYear, latestMonth - 12, 1);
    return dated.filter((item) => {
      const [year, month] = item.key.split('-').map(Number);
      const date = new Date(year, month - 1, 1);
      return date >= previousCutoff && date < recentCutoff;
    }).length;
  }, [incidents]);

  if (!factory) {
    return (
      <div className="page">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(factoryListPath)}>
          {t('incident.detail.actions.back')}
        </Button>
        <Empty description={t('factory.detail.notFound')} />
      </div>
    );
  }

  const productColumns: ColumnsType<FactoryProductSummary> = [
    { title: t('factory.products.productName'), dataIndex: 'product_name' },
    { title: t('factory.products.modelNumber'), dataIndex: 'model_number' },
    { title: t('factory.products.lotCount'), dataIndex: 'lot_count', width: 120 },
    { title: t('factory.products.unitCount'), dataIndex: 'unit_count', width: 140 },
    {
      title: t('factory.products.newProduct'),
      dataIndex: 'is_new_product',
      width: 130,
      render: (value) => (value ? t('common.yes') : t('common.no')),
    },
  ];

  const recentDelta = previousPeriodCount === 0
    ? recentIncidents.length
    : Math.round(((recentIncidents.length - previousPeriodCount) / previousPeriodCount) * 100);

  return (
    <div className="page factoryDetailPage">
      <div className="backRow">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(factoryListPath)}>
          {t('incident.detail.actions.back')}
        </Button>
      </div>
      <div className="pageMetaBar pageMetaBarRow">
        <Text type="secondary">{[factory.name, factory.code, factory.region_code, factory.organization_name].filter(Boolean).join(' / ')}</Text>
        {factory.official_url && (
          <Button icon={<LinkOutlined />} href={factory.official_url} target="_blank">
            {t('master.actions.officialUrl')}
          </Button>
        )}
      </div>
      <Tabs
        defaultActiveKey="summary"
        items={[
          {
            key: 'summary',
            label: t('factory.tabs.summary'),
            children: (
              <div className="factorySummaryTab">
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={6}><Card><Statistic title={t('master.stats.incidents')} value={factory.incident_count} /></Card></Col>
                  <Col xs={24} md={6}><Card><Statistic title={t('master.stats.openIncidents')} value={factory.open_incident_count} /></Card></Col>
                  <Col xs={24} md={6}><Card><Statistic title={t('factory.summary.recentIncidents')} value={recentIncidents.length} /></Card></Col>
                  <Col xs={24} md={6}>
                    <Card>
                      <Statistic
                        title={t('factory.summary.recentDelta')}
                        value={recentDelta}
                        suffix={previousPeriodCount === 0 ? '' : '%'}
                        valueStyle={{ color: recentDelta > 0 ? '#dc2626' : '#16a34a' }}
                      />
                    </Card>
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col xs={24} xl={16}>
                    <Card title={t('factory.summary.monthlyTrend')}>
                      <MonthlyIncidentChart buckets={monthlyBuckets} />
                    </Card>
                  </Col>
                  <Col xs={24} xl={8}>
                    <Card title={t('factory.summary.recentStatusRatio')} extra={<Tag>{t('factory.summary.recentWindow')}</Tag>}>
                      <RecentStatusDonut slices={recentSlices} />
                    </Card>
                  </Col>
                </Row>
                <Card title={t('factory.summary.defectHeatmap')}>
                  <DefectHeatmap rows={heatmapRows} months={monthlyBuckets} />
                </Card>
              </div>
            ),
          },
          {
            key: 'basic',
            label: t('master.sections.basicInfo'),
            children: (
              <Card>
                <Descriptions column={{ xs: 1, md: 2 }} bordered>
                  <Descriptions.Item label={t('factory.columns.organization')}>{factory.organization_name || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('factory.columns.region')}>{factory.region_code || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('factory.columns.country')}>{factory.country_code || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('factory.columns.hvac')}>{factory.is_hvac ? t('common.yes') : t('common.no')}</Descriptions.Item>
                  <Descriptions.Item label={t('factory.columns.address')} span={2}>{factory.address || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('factory.columns.productionProducts')} span={2}>{factory.production_products || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('factory.columns.notes')} span={2}>{factory.notes || '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('factory.columns.latitude')}>{factory.latitude ?? '-'}</Descriptions.Item>
                  <Descriptions.Item label={t('factory.columns.longitude')}>{factory.longitude ?? '-'}</Descriptions.Item>
                </Descriptions>
              </Card>
            ),
          },
          {
            key: 'products',
            label: t('factory.sections.products'),
            children: (
              <Card>
                <Table rowKey="product_id" columns={productColumns} dataSource={factory.products} pagination={{ pageSize: 8 }} />
              </Card>
            ),
          },
          {
            key: 'incidents',
            label: t('factory.sections.incidents'),
            children: (
              <Card>
                <IncidentListTable
                  incidents={incidents}
                  attachmentsByIncident={attachmentsByIncident}
                  tasksByIncident={tasksByIncident}
                  currentUser={currentUser}
                  fixedContextLabel={factory.name}
                  showRoleFilters={false}
                />
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
};

export default FactoryDetailPage;
